import React, { useEffect, useState, useCallback } from "react";
import { CharacterService } from "../../services/characterService";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import { Character, CharacterDiceTemplate } from "../../types/types";

interface SessionCharacterPanelProps {
  characterId?: string;
  sessionId: string;
  className?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildFormula = (notation: string, modifier: number): string => {
  if (modifier > 0) return `${notation}+${modifier}`;
  if (modifier < 0) return `${notation}${modifier}`;
  return notation;
};

// Skill-type groups with display order, icon, and label
type SkillGroup = "attack" | "damage" | "save" | "check" | "other";

const SKILL_GROUPS: { key: SkillGroup; icon: string; label: string }[] = [
  { key: "attack", icon: "⚔️", label: "Attacks" },
  { key: "damage", icon: "💥", label: "Damage" },
  { key: "save", icon: "🛡️", label: "Saves" },
  { key: "check", icon: "🎯", label: "Checks" },
  { key: "other", icon: "🎲", label: "Other" },
];

const SKILL_STYLES: Record<SkillGroup, { badge: string; btn: string; btnHover: string }> = {
  attack: {
    badge: "bg-red-900/60 text-red-300 border-red-700",
    btn: "border-zinc-700 bg-zinc-800/80",
    btnHover: "hover:border-red-600 hover:bg-red-900/30",
  },
  damage: {
    badge: "bg-orange-900/60 text-orange-300 border-orange-700",
    btn: "border-zinc-700 bg-zinc-800/80",
    btnHover: "hover:border-orange-600 hover:bg-orange-900/30",
  },
  save: {
    badge: "bg-yellow-900/60 text-yellow-300 border-yellow-700",
    btn: "border-zinc-700 bg-zinc-800/80",
    btnHover: "hover:border-yellow-600 hover:bg-yellow-900/30",
  },
  check: {
    badge: "bg-blue-900/60 text-blue-300 border-blue-700",
    btn: "border-zinc-700 bg-zinc-800/80",
    btnHover: "hover:border-blue-600 hover:bg-blue-900/30",
  },
  other: {
    badge: "bg-zinc-700/60 text-zinc-300 border-zinc-600",
    btn: "border-zinc-700 bg-zinc-800/80",
    btnHover: "hover:border-zinc-500 hover:bg-zinc-700/60",
  },
};

const resolveGroup = (skillType: string): SkillGroup => {
  const key = (skillType || "").toLowerCase();
  if (key.includes("attack")) return "attack";
  if (key.includes("damage")) return "damage";
  if (key.includes("save")) return "save";
  if (key.includes("check")) return "check";
  return "other";
};

// ─── Component ────────────────────────────────────────────────────────────────

export const SessionCharacterPanel: React.FC<SessionCharacterPanelProps> = ({
  characterId,
  sessionId,
  className = "",
}) => {
  const { isConnected, sendDiceRoll } = useWebSocketContext();

  const [character, setCharacter] = useState<Character | null>(null);
  const [templates, setTemplates] = useState<CharacterDiceTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNotes, setShowNotes] = useState(false);

  // Custom roll state
  const [customFormula, setCustomFormula] = useState("1d20");
  const [customRollName, setCustomRollName] = useState("");
  const [isRolling, setIsRolling] = useState<number | string | null>(null);

  const fetchCharacter = useCallback(async () => {
    if (!characterId) return;
    const numId = parseInt(characterId, 10);
    if (isNaN(numId)) return;

    setLoading(true);
    setError(null);
    try {
      const [char, tmpl] = await Promise.all([
        CharacterService.getCharacter(numId),
        CharacterService.getCharacterDiceTemplates(numId),
      ]);
      setCharacter(char);
      setTemplates(tmpl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load character");
    } finally {
      setLoading(false);
    }
  }, [characterId]);

  useEffect(() => {
    fetchCharacter();
  }, [fetchCharacter]);

  const handleTemplateRoll = (template: CharacterDiceTemplate) => {
    if (!isConnected || !character) return;
    setIsRolling(template.id);
    const formula = buildFormula(template.diceNotation, template.modifier);
    sendDiceRoll(formula, template.name, false, characterId, sessionId, character.name);
    setTimeout(() => setIsRolling(null), 600);
  };

  const handleCustomRoll = () => {
    if (!isConnected || !customFormula.trim()) return;
    setIsRolling("custom");
    sendDiceRoll(
      customFormula.trim(),
      customRollName.trim() || undefined,
      false,
      characterId,
      sessionId,
      character?.name
    );
    setCustomRollName("");
    setTimeout(() => setIsRolling(null), 600);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleCustomRoll();
  };

  // ─── Early exit states ─────────────────────────────────────────────────────

  if (!characterId) {
    return (
      <div className={`p-6 text-center ${className}`}>
        <div className="text-5xl mb-3">📋</div>
        <p className="text-zinc-400 text-sm">No character assigned to this session.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`p-6 text-center ${className}`}>
        <div className="animate-spin text-4xl mb-3">⚙️</div>
        <p className="text-zinc-400 text-sm">Loading character…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 ${className}`}>
        <p className="text-red-400 text-sm mb-3">{error}</p>
        <button
          onClick={fetchCharacter}
          className="text-xs px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!character) return null;

  // Group templates
  const grouped = SKILL_GROUPS.map(({ key, icon, label }) => ({
    key,
    icon,
    label,
    items: templates.filter((t) => resolveGroup(t.skillType) === key),
  })).filter((g) => g.items.length > 0);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={`flex flex-col overflow-y-auto codex-scroll ${className}`}
      style={{ backgroundColor: "#18181b" }}
    >
      {/* ── Identity banner ───────────────────────────────────────────────── */}
      <div
        className="flex items-center space-x-4 p-4 border-b border-zinc-700/60"
        style={{ background: "linear-gradient(135deg,#1f1f2e 0%,#23232f 100%)" }}
      >
        {character.imageUrl ? (
          <img
            src={character.imageUrl}
            alt={character.name}
            className="w-16 h-16 rounded-full object-cover border-2 border-amber-500/70 flex-shrink-0 shadow-lg"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-amber-900/40 border-2 border-amber-500/70 flex items-center justify-center text-3xl flex-shrink-0 shadow-lg">
            🧙
          </div>
        )}

        <div className="min-w-0 flex-1">
          <h3 className="text-amber-400 font-bold text-lg leading-tight truncate">
            {character.name}
          </h3>
          {character.description && (
            <p className="text-zinc-400 text-xs mt-1 line-clamp-2 leading-relaxed">
              {character.description}
            </p>
          )}
          {character.notes && (
            <button
              onClick={() => setShowNotes((v) => !v)}
              className="mt-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              {showNotes ? "▲ Hide notes" : "▼ Show notes"}
            </button>
          )}
        </div>
      </div>

      {/* Notes drawer */}
      {showNotes && character.notes && (
        <div className="px-4 py-3 border-b border-zinc-700/60 bg-zinc-900/60">
          <p className="text-zinc-300 text-xs leading-relaxed whitespace-pre-wrap">
            {character.notes}
          </p>
        </div>
      )}

      {/* ── Dice template groups ───────────────────────────────────────────── */}
      <div className="flex-1 px-3 py-3 space-y-4">
        {grouped.length === 0 && (
          <p className="text-zinc-500 text-xs italic px-1 py-2">
            No fate formulas yet. Add them on the hero page.
          </p>
        )}

        {grouped.map(({ key, icon, label, items }) => {
          const styles = SKILL_STYLES[key];
          return (
            <div key={key}>
              {/* Group header */}
              <div className="flex items-center space-x-1.5 mb-1.5">
                <span className="text-sm">{icon}</span>
                <span className="text-xs font-semibold uppercase tracking-widest text-zinc-400">
                  {label}
                </span>
              </div>

              {/* Template buttons */}
              <div className="space-y-1">
                {items.map((tpl) => {
                  const formula = buildFormula(tpl.diceNotation, tpl.modifier);
                  const rolling = isRolling === tpl.id;
                  return (
                    <button
                      key={tpl.id}
                      onClick={() => handleTemplateRoll(tpl)}
                      disabled={!isConnected || rolling}
                      title={tpl.description || formula}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-all active:scale-95 ${styles.btn} ${styles.btnHover} ${
                        rolling ? "opacity-80 scale-95" : ""
                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-center space-x-2 min-w-0">
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded border flex-shrink-0 ${styles.badge}`}
                        >
                          {tpl.skillType || "roll"}
                        </span>
                        <span className="text-zinc-100 font-medium truncate">{tpl.name}</span>
                      </div>
                      <span className="text-amber-400 font-mono text-xs flex-shrink-0 ml-2">
                        {rolling ? "🎲" : formula}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* ── Custom Roll ─────────────────────────────────────────────────── */}
        <div className="border-t border-zinc-700/60 pt-4">
          <div className="flex items-center space-x-1.5 mb-2">
            <span className="text-sm">✏️</span>
            <span className="text-xs font-semibold uppercase tracking-widest text-zinc-400">
              Custom Roll
            </span>
          </div>

          <div className="space-y-2">
            <input
              type="text"
              value={customFormula}
              onChange={(e) => setCustomFormula(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. 1d20+5, 2d6, 4d6kh3"
              className="w-full px-3 py-2 bg-zinc-900 text-white placeholder-zinc-600 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none text-sm font-mono transition-colors"
            />
            <input
              type="text"
              value={customRollName}
              onChange={(e) => setCustomRollName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Roll name (optional)"
              className="w-full px-3 py-2 bg-zinc-900 text-white placeholder-zinc-600 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none text-sm transition-colors"
            />
            <button
              onClick={handleCustomRoll}
              disabled={!isConnected || !customFormula.trim() || isRolling === "custom"}
              className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all active:scale-95 ${
                isRolling === "custom" ? "bg-amber-700 scale-95" : "bg-amber-600 hover:bg-amber-500"
              } text-white disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {isRolling === "custom" ? "🎲 Rolling…" : `🎲 Roll as ${character.name}`}
            </button>
          </div>

          {!isConnected && (
            <p className="text-red-400 text-xs mt-2 flex items-center space-x-1">
              <span>⚠️</span>
              <span>Disconnected — cannot roll</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
